import { Col, DatePicker, Form, FormInstance, Input, InputNumber, Row } from 'antd';
import { TFunction } from 'i18next';
import { disableFutureDates } from '../../../utils/disableDates';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import DefaultButton from '../../../components/buttons/DefaultButton';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import { useEffect, useRef, useState } from 'react';
import { useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { REGEX_ALL_ALLOWED_SAFE_FILTER, REGEX_ALPHA_NUMERIC_DASH_SPACE_SAFE_FILTER, REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER } from '../../../constants/regex';

interface GeneralProductInformationProps {
  t: TFunction;
  form: FormInstance;
  disabled?: boolean;
  isViewMode?: boolean;
  handleCancel?: () => void;
  handleBack?: () => void;
  handleNext?: () => void;
}

const GeneralProductInformation: React.FC<GeneralProductInformationProps> = ({
  t,
  form,
  disabled,
  isViewMode = false,
  handleCancel,
  handleBack,
  handleNext,
}) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [enableNext, setEnableNext] = useState<boolean>(false);

  const generalInfoFormValues = useWatch([], form);

  const toggleCancelModal = () => {
    isCancelModalOpen ? setIsCancelModalOpen(false) : setIsCancelModalOpen(true);
  };

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setEnableNext(true))
      .catch(() => setEnableNext(false));
  }, [form, generalInfoFormValues]);

  return (
    <div className="m-0 w-full p-4">
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('generalProductInformationHeading')}
        </h2>
      </div>

      <div className="">
        {/* <div className="rounded-lg border border-gray-200 p-4 md:p-8"> */}
        <Form
          form={form}
          name="generalInfoFormData"
          layout="vertical"
          disabled={disabled || isViewMode}
          className={isViewMode ? 'view-mode-form' : ''}
          initialValues={{
            date: dayjs(),
          }}
        >
          <Row gutter={[24, 8]}>
            <Col xs={24} md={12}>
              {/* Verification Record for Pre-Packaged Products */}
              <Form.Item
                label={t('prePackagedProductVerificationRecord')}
                name="verificationRecord"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('productVerificationRecordRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('productVerificationRecordSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: any) => {
                      if (!value) return Promise.resolve();
                      // Allowing letters, numbers, dash and space
                      // const regex = /  ^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9\-\s]*$/;
                      const regex = REGEX_ALPHA_NUMERIC_DASH_SPACE_SAFE_FILTER;
                      return regex.test(String(value.trim()))
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 3) {
                        return Promise.reject(t('productVerificationRecordMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('productVerificationRecordMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className="mobile-label-item"
              >
                <Input placeholder={t('productVerificationRecordPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {/* Company Name */}
              <Form.Item
                label={t('companyName')}
                name="companyName"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('companyNameRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('companyNameSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: any) => {
                      if (!value) return Promise.resolve();
                      // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis
                      // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9 ,\-\/\.\(\)]*$/;
                      const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                      return regex.test(String(value))
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 3) {
                        return Promise.reject(t('companyNameMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('companyNameMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={t('companyNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {/* Date */}
              <Form.Item
                label={t('date')}
                name="date"
                rules={[
                  {
                    required: true,
                    message: t('dateRequired'),
                  },
                ]}
              >
                <DatePicker
                  className="w-full"
                  showTime={false}
                  showSecond={false}
                  format="YYYY-MM-DD"
                  disabledDate={disableFutureDates}
                  placeholder={t('datePlaceholder')}
                  inputReadOnly={true}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Product Name */}
              <Form.Item
                label={t('productName')}
                name="productName"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('productNameRequired'),
                  },
                ]}
                className="view-mode-form"
              >
                <Input placeholder={t('productNamePlaceholder')} disabled />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Nominal Amount of Nutrients (Qn) */}
              <Form.Item
                label={t('nominalAmountOfNutrients')}
                name="nominalAmountOfNutrients"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('nominalAmountOfNutrientsRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('nominalAmountOfNutrientsSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  /* {
                    validator: (_: any, value: any) => {
                      if (!value) return Promise.resolve();
                      // value should start with number, one space optional and optionally ends with letters or %. example 500, 12.5, 10 mg, 15%
                      const regex = /^\d+(\.\d+)? ?([A-Za-zÁÉÍÓÚáéíóúÑñÜü]+|%)?$/;
                      return regex.test(String(value))
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  }, */
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      // Allow letters, numbers, space, punctuation and special characters               
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('nominalAmountOfNutrientsMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('nominalAmountOfNutrientsMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className="mobile-label-item"
              >
                <Input placeholder={t('nominalAmountOfNutrientsPlaceholder')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Tolerable Deficiency of Nutrients (Qn) */}
              <Form.Item
                label={t('tolerableDeficiencyOfNutrients')}
                name="tolerableDeficiencyOfNutrients"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('tolerableDeficiencyOfNutrientsRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('tolerableDeficiencyOfNutrientsSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      // Allow letters, numbers, space, punctuation and special characters               
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('tolerableDeficiencyOfNutrientMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('tolerableDeficiencyOfNutrientMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className="mobile-label-item"
              >
                <Input placeholder={t('tolerableDeficiencyOfNutrientsPlaceholder')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Batch */}
              <Form.Item
                label={t('batch')}
                name="batch"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('batchRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('batchSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      // Allow letters, numbers, space, punctuation and special characters               
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('batchMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('batchMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={t('batchPlaceholder')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Sample Size */}
              <Form.Item
                label={t('sampleSize')}
                name="sampleSize"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('sampleSizeRequired'),
                  },
                  {
                    pattern: /^[0-9]*$/,
                    message: t('invalidInput'),
                  },
                  {
                    validator: (_, value) => {
                      if (value <= 0) {
                        return Promise.reject(new Error(t('sampleSizePositiveValue')));
                      }
                      // MAX_SAFE_INTEGER is 9007199254740991
                      if(value > Number.MAX_SAFE_INTEGER){
                        return Promise.reject(
                          new Error(t('invalidInput'))
                        );
                      }
                      if (!Number.isInteger(value)) {
                        return Promise.reject(new Error(t('sampleSizeWholeNumber')));
                      }
                      return Promise.resolve();
                    },
                  },
                  /* {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('sampleSizeSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  }, */
                ]}
              >
                {/* <Input placeholder={t('sampleSizePlaceholder')} /> */}
                <InputNumber
                  type="number"
                  placeholder={t('sampleSizePlaceholder')}
                  className="w-full"
                  onWheel={(e) => e.currentTarget.blur()}
                  controls={true}
                  precision={0}
                  min={0}
                  // stringMode
                  onPaste={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'e' ||
                      e.key === 'E' ||
                      e.key === '-' ||
                      e.key === '+' ||
                      e.key === '.'
                    ) {
                      e.preventDefault();
                    }
                    const allowedKeys = [
                      'Backspace',
                      'Enter',
                      'Escape',
                      'ArrowLeft',
                      'ArrowRight',
                      'Delete',
                      'End',
                      'Home',
                    ];

                    const isDigit = /[0-9]/.test(e.key);
                    if (!allowedKeys && !isDigit) {
                      e.preventDefault();
                    }
                  }}
                  formatter={(value) => `${value}`.replace(/\D/g, '')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Location of Sampling */}
              <Form.Item
                label={t('locationOfSampling')}
                name="locationOfSampling"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('locationOfSamplingRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('locationOfSamplingSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      // Allow letters, numbers, space, punctuation and special characters               
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 3) {
                        return Promise.reject(t('locationOfSamplingMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('locationOfSamplingMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={t('locationOfSamplingPlaceholder')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Product Brand */}
              <Form.Item
                label={t('productBrand')}
                name="productBrand"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('productBrandRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('productBrandSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      // Allow letters, numbers, space, punctuation and special characters               
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 3) {
                        return Promise.reject(t('productBrandMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('productBrandMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={t('productBrandPlaceholder')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Non-compliant prepackaged items permitted in the sample */}
              <Form.Item
                name="nonCompliantPrePackagedItemsPermitted"
                label={t('nonCompliantPrePackagedItems')}
                required={true}
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('nonCompliantPrePackagedItemsRequired'),
                  },
                  {
                    pattern: /^[0-9]*$/,
                    message: t('invalidInput'),
                  },
                  // {
                  //   type: 'integer',
                  //   min: 1,
                  //   message: t('nonCompliantPrePackagedItemsPositiveValue')
                  // },
                  {
                    validator: (_, value) => {
                      if (value <= 0) {
                        return Promise.reject(
                          new Error(t('nonCompliantPrePackagedItemsPositiveValue'))
                        );
                      }
                      // MAX_SAFE_INTEGER is 9007199254740991
                      if(value > Number.MAX_SAFE_INTEGER){
                        return Promise.reject(
                          new Error(t('invalidInput'))
                        );
                      }
                      if (!Number.isInteger(value)) {
                        return Promise.reject(
                          new Error(t('nonCompliantPrePackagedItemsWholeNumber'))
                        );
                      }
                      /*  if (!value || BigInt(value) > 0n) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('nonCompliantPrePackagedItemsPositiveValue'))); */
                      return Promise.resolve();
                    },
                  },
                ]}
                className="mobile-label-item"
              >
                <InputNumber
                  type="number"
                  placeholder={t('nonCompliantPrePackagedItemsPlaceholder')}
                  className="w-full"
                  onWheel={(e) => e.currentTarget.blur()}
                  controls={true}
                  precision={0}
                  min={0}
                  // stringMode
                  onPaste={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'e' ||
                      e.key === 'E' ||
                      e.key === '-' ||
                      e.key === '+' ||
                      e.key === '.'
                    ) {
                      e.preventDefault();
                    }
                    const allowedKeys = [
                      'Backspace',
                      'Enter',
                      'Escape',
                      'ArrowLeft',
                      'ArrowRight',
                      'Delete',
                      'End',
                      'Home',
                    ];

                    const isDigit = /[0-9]/.test(e.key);
                    if (!allowedKeys && !isDigit) {
                      e.preventDefault();
                    }
                  }}
                  formatter={(value) => `${value}`.replace(/\D/g, '')}
                  // parser={(value) => value?.replace(/\D/g, '') || 0}
                  // formatter={(value) => {
                  //   if (!value) return '';
                  //   return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
                  // }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {/* Prouction Batch */}
              <Form.Item
                label={t('productBatch')}
                name="productionBatch"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: t('productBatchRequired'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('productBatchSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      // Allow letters, numbers, space, punctuation and special characters               
                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                      return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 1) {
                        return Promise.reject(t('productBatchMinValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('productBatchdMaxValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={t('productBatchPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {!isViewMode && (
          <div className="flex w-full justify-end gap-6">
            <div className="mt-6 flex w-full flex-col justify-end gap-4 md:w-1/2 md:flex-row md:pl-3">
              <DefaultButton handleOnClick={toggleCancelModal} width="100%">
                {t('cancel')}
              </DefaultButton>
              {enableNext ? (
                <PrimaryButton
                  handleOnClick={(e) => {
                    e.preventDefault();
                    handleNext?.();
                  }}
                  width="100%"
                  disabled={disabled || isViewMode}
                  hideBgGradient={true}
                >
                  {t('next')}
                </PrimaryButton>
              ) : (
                <SecondaryButton
                  handleOnClick={(e) => {
                    e.preventDefault();
                    handleNext?.();
                  }}
                  width="100%"
                  disabled={!enableNext || isViewMode}
                >
                  {t('next')}
                </SecondaryButton>
              )}
            </div>
          </div>
        )}

        <ConfirmModelComponent
          isOpen={isCancelModalOpen}
          onClickedCancel={toggleCancelModal}
          header={t('verificationCancelModalTitle')}
          content={<p>{t('verificationCancelModalConfirmaiton')}</p>}
          footerAction={{
            confimation: {
              label: t('yes'),
              onClick: handleCancel ?? (() => {}),
            },
            cancel: {
              label: t('no'),
              onClick: toggleCancelModal,
            },
          }}
          styleClasses="z-100"
        />
      </div>
    </div>
  );
};

export default GeneralProductInformation;
